import React, { useEffect, useMemo, useState } from "react";
import {
  AlertTriangle,
  Bot,
  CheckCircle2,
  ChevronLeft,
  ChevronRight,
  CircleDollarSign,
  Clock3,
  Landmark,
  Mail,
  ReceiptText,
  ShieldCheck,
  Sparkles,
  X,
} from "lucide-react";
import ScreenOverlay from "../common/ScreenOverlay";

/**
 * CENTRAL SIMPLER
 *
 * Objetivo:
 * - Hero operacional do escritório
 * - Cards rápidos de atenção
 * - Banner inteligente rotativo
 * - Drawer lateral sem sair do dashboard
 *
 * Os dados abaixo são fallback visual.
 * Depois podem ser substituídos pelos dados reais do backend.
 */

const FALLBACK_DATA = {
  companies: 184,
  monitored: 184,
  critical: 12,
  fiscalRegular: 0,
  fiscalPending: 0,
  obligations: 7,
  certificates: 3,
  certificatesRegular: 0,
  messages: 5,
  financial: 2,
  lastSync: "há 4 min",

  fiscalItems: [
    {
      id: 1,
      title: "Pendências na PGFN",
      description: "3 empresas precisam de atenção",
      count: 3,
      severity: "critical",
    },
    {
      id: 2,
      title: "DCTFWeb não entregue",
      description: "4 competências pendentes",
      count: 4,
      severity: "warning",
    },
    {
      id: 3,
      title: "MIT não entregue",
      description: "2 empresas com obrigação pendente",
      count: 2,
      severity: "warning",
    },
    {
      id: 4,
      title: "Certidões",
      description: "3 certidões próximas do vencimento",
      count: 3,
      severity: "info",
    },
  ],

  obligationItems: [
    {
      id: 1,
      title: "DCTFWeb",
      description: "Vencimento próximo",
      count: 4,
      severity: "warning",
    },
    {
      id: 2,
      title: "MIT",
      description: "Empresas ainda não entregues",
      count: 2,
      severity: "warning",
    },
    {
      id: 3,
      title: "PGDAS-D",
      description: "Competências que precisam de revisão",
      count: 1,
      severity: "info",
    },
  ],

  certificateItems: [
    {
      id: 1,
      title: "Certidão Federal",
      description: "Vence em até 15 dias",
      count: 2,
      severity: "warning",
    },
    {
      id: 2,
      title: "Regularidade fiscal",
      description: "Empresa requer nova consulta",
      count: 1,
      severity: "info",
    },
  ],

  messageItems: [
    {
      id: 1,
      title: "Caixa Postal e-CAC",
      description: "5 novas mensagens encontradas",
      count: 5,
      severity: "info",
    },
  ],

  financialItems: [
    {
      id: 1,
      title: "Situações financeiras",
      description: "2 ocorrências para revisão",
      count: 2,
      severity: "warning",
    },
  ],
};

function formatNumber(value) {
  try {
    return new Intl.NumberFormat("pt-BR").format(Number(value || 0));
  } catch {
    return value || 0;
  }
}

function SeverityIcon({ severity }) {
  if (severity === "critical") {
    return <AlertTriangle className="h-4 w-4" />;
  }

  if (severity === "warning") {
    return <Clock3 className="h-4 w-4" />;
  }

  return <CheckCircle2 className="h-4 w-4" />;
}

function StatusBadge({ severity }) {
  const styles = {
    critical:
      "border-red-200 bg-red-50 text-red-700",
    warning:
      "border-amber-200 bg-amber-50 text-amber-700",
    info:
      "border-violet-200 bg-violet-50 text-violet-700",
    success:
      "border-emerald-200 bg-emerald-50 text-emerald-700",
  };

  const labels = {
    critical: "Crítico",
    warning: "Atenção",
    info: "Revisar",
    success: "Regular",
  };

  return (
    <span
      className={[
        "inline-flex items-center rounded-full border px-2 py-0.5",
        "text-[10px] font-semibold uppercase tracking-[0.08em]",
        styles[severity] || styles.info,
      ].join(" ")}
    >
      {labels[severity] || "Revisar"}
    </span>
  );
}

function QuickAction({
  icon: Icon,
  title,
  subtitle,
  count,
  active,
  onClick,
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={[
        "group relative flex min-w-0 flex-col items-center justify-center",
        "gap-2 rounded-2xl px-2 py-3 text-center",
        "transition-all duration-300",
        active
          ? "bg-violet-50 dark:bg-violet-500/10"
          : "hover:bg-slate-50 dark:hover:bg-white/[0.035]",
      ].join(" ")}
    >
      <div className="relative">
        <div
          className={[
            "flex h-[52px] w-[52px] items-center justify-center rounded-full",
            "border shadow-[0_5px_16px_rgba(15,23,42,0.06)]",
            "transition-all duration-300",
            active
              ? "scale-105 border-violet-300 bg-violet-600 text-white shadow-[0_8px_22px_rgba(124,58,237,0.22)]"
              : "border-slate-200 bg-white text-slate-600 group-hover:-translate-y-1 group-hover:border-violet-200 group-hover:text-violet-700 group-hover:shadow-[0_10px_24px_rgba(15,23,42,0.09)] dark:border-white/[0.12] dark:bg-[#151a23] dark:text-zinc-200 dark:shadow-none dark:group-hover:border-violet-400/25 dark:group-hover:bg-[#191e28] dark:group-hover:text-violet-300",
          ].join(" ")}
        >
          <Icon
            className="h-[21px] w-[21px]"
            strokeWidth={1.8}
          />
        </div>

        {Number(count) > 0 && (
          <span
            className={[
              "absolute -right-1 -top-1",
              "flex min-h-[20px] min-w-[20px] items-center justify-center",
              "rounded-full border-2 border-white bg-violet-600 px-1 dark:border-[#11151d]",
              "text-[9px] font-bold leading-none text-white",
              "shadow-sm",
            ].join(" ")}
          >
            {count > 99 ? "99+" : count}
          </span>
        )}
      </div>

      <div className="min-w-0">
        <div className="truncate text-[11px] font-semibold text-slate-800 dark:text-zinc-100">
          {title}
        </div>

        <div className="mt-0.5 hidden truncate text-[9px] text-slate-400 dark:text-zinc-300/80 xl:block">
          {subtitle}
        </div>
      </div>
    </button>
  );
}

function DrawerItem({ item, onOpen }) {
  return (
    <button
      type="button"
      onClick={() => onOpen?.(item)}
      className={[
        "group flex w-full items-center gap-3 rounded-xl",
        "border border-slate-200 bg-white p-3.5 text-left",
        "transition-all duration-200",
        "hover:border-violet-200 hover:bg-violet-50/40",
      ].join(" ")}
    >
      <div
        className={[
          "flex h-9 w-9 shrink-0 items-center justify-center rounded-xl",
          item.severity === "critical"
            ? "bg-red-50 text-red-600"
            : item.severity === "warning"
            ? "bg-amber-50 text-amber-600"
            : "bg-violet-50 text-violet-600",
        ].join(" ")}
      >
        <SeverityIcon severity={item.severity} />
      </div>

      <div className="min-w-0 flex-1">
        <div className="flex items-center gap-2">
          <p className="truncate text-[13px] font-semibold text-slate-900">
            {item.title}
          </p>

          <StatusBadge severity={item.severity} />
        </div>

        <p className="mt-1 truncate text-[11px] text-slate-500">
          {item.description}
        </p>
      </div>

      {item.count !== undefined && (
        <div className="mr-1 text-sm font-bold text-slate-900">
          {item.count}
        </div>
      )}

      <ChevronRight
        className="h-4 w-4 text-slate-300 transition-transform group-hover:translate-x-0.5 group-hover:text-violet-500"
      />
    </button>
  );
}

export default function SimplerCommandCenter({
  data: externalData,
  sisiImage,
  onNavigate,
}) {
  const data = useMemo(
    () => ({
      ...FALLBACK_DATA,
      ...(externalData || {}),
    }),
    [externalData]
  );

  const [activeSlide, setActiveSlide] = useState(0);
  const [drawer, setDrawer] = useState(null);

  const slides = useMemo(
    () => [
      {
        id: "overview",
        eyebrow: "Visão do escritório",
        title:
          data.critical > 0
            ? `${data.critical} situações precisam da sua atenção`
            : "Tudo sob controle hoje.",
        description:
          data.critical > 0
            ? `A Simpler monitora ${formatNumber(
                data.monitored
              )} CNPJs e encontrou ocorrências que merecem revisão.`
            : `A Simpler está monitorando ${formatNumber(
                data.monitored
              )} CNPJs sem ocorrências críticas no momento.`,
        cta:
          data.critical > 0
            ? "Ver ocorrências"
            : "Ver monitoramento",
        type: "overview",
      },

      {
        id: "obligations",
        eyebrow: "Agenda fiscal inteligente",
        title: `${data.obligations} obrigações estão próximas`,
        description:
          "Antecipe vencimentos, identifique empresas pendentes e concentre as ações fiscais em um único lugar.",
        cta: "Ver obrigações",
        type: "obligations",
      },

      {
        id: "automation",
        eyebrow: "Monitoramento automático",
        title: "Enquanto você trabalha, a Simpler monitora.",
        description: `e-CAC e módulos fiscais acompanhados automaticamente. Última sincronização ${data.lastSync}.`,
        cta: "Ver monitoramento fiscal",
        type: "automation",
      },
    ],
    [data]
  );

  useEffect(() => {
    const interval = window.setInterval(() => {
      setActiveSlide((current) => (current + 1) % slides.length);
    }, 7000);

    return () => window.clearInterval(interval);
  }, [slides.length]);

  const slide = slides[activeSlide];

  const drawerConfig = {
    messages: {
      title: "Caixa Postal",
      description: "Mensagens e comunicações encontradas",
      icon: Mail,
      items: data.messageItems || [],
      route: "/app/mensagens",
    },

    fiscal: {
      title: "Situação Fiscal",
      description: "Regularidade fiscal no e-CAC",
      icon: Landmark,
      items: data.fiscalItems || [],
      route: "/app/serpro/sitfis",
    },

    obligations: {
      title: "Obrigações",
      description: "Prazos e entregas do escritório",
      icon: ReceiptText,
      items: data.obligationItems || [],
      route: "/app/obrigacoes",
    },

    certificates: {
      title: "Certidões",
      description: "Validade e regularidade fiscal",
      icon: ShieldCheck,
      items: data.certificateItems || [],
      route: "/app/certidoes",
    },

    financial: {
      title: "Financeiro Fiscal",
      description: "Ocorrências financeiras monitoradas",
      icon: CircleDollarSign,
      items: data.financialItems || [],
      route: "/app/debitos",
    },

    sisi: {
      title: "Sisi",
      description: "Inteligência fiscal da Simpler",
      icon: Bot,
      items: [
        {
          id: "sisi-1",
          title: "Resumo inteligente",
          description: `${data.critical} situações merecem atenção no escritório.`,
          severity: data.critical > 0 ? "warning" : "success",
        },
        {
          id: "sisi-2",
          title: "Monitoramento ativo",
          description: `${formatNumber(
            data.monitored
          )} CNPJs estão sendo acompanhados.`,
          severity: "info",
        },
      ],
      route: "/app/simpler-ai",
    },
  };

  const currentDrawer = drawer ? drawerConfig[drawer] : null;

  function handleHeroAction() {
    if (slide.type === "overview") {
      setDrawer("fiscal");
      return;
    }

    if (slide.type === "obligations") {
      setDrawer("obligations");
      return;
    }

    onNavigate?.("/app/dashboard-federal");
  }

  function nextSlide() {
    setActiveSlide((current) => (current + 1) % slides.length);
  }

  function previousSlide() {
    setActiveSlide(
      (current) => (current - 1 + slides.length) % slides.length
    );
  }

  return (
    <>
      <section className="space-y-4">
        {/* ==========================================================
            HERO / CENTRAL SIMPLER
        ========================================================== */}
        <div
          className={[
            "relative overflow-hidden rounded-[26px]",
            "border border-slate-200 bg-white dark:border-white/10 dark:bg-[#11151d]",
            "shadow-[0_18px_60px_rgba(15,23,42,0.07)] dark:shadow-none",
          ].join(" ")}
        >
          {/* elementos decorativos próprios */}
          <div className="pointer-events-none absolute -right-24 -top-24 h-72 w-72 rounded-full bg-violet-100/60 blur-3xl dark:bg-violet-700/10 dark:opacity-60" />
          <div className="pointer-events-none absolute bottom-[-120px] left-[25%] h-64 w-64 rounded-full bg-fuchsia-50 blur-3xl dark:bg-violet-950/20 dark:opacity-30" />

          <div className="relative grid min-h-[340px] grid-cols-1 lg:grid-cols-[1.08fr_0.92fr]">
            {/* ESQUERDA */}
            <div className="flex flex-col justify-between p-6 sm:p-8 lg:p-9">
              <div>
                <div
                  className={[
                    "mb-5 inline-flex items-center gap-2 rounded-full",
                    "border border-violet-200 bg-violet-50 px-3 py-1.5 dark:border-violet-400/20 dark:bg-violet-500/10",
                    "text-[11px] font-semibold text-violet-700 dark:text-violet-300",
                  ].join(" ")}
                >
                  <Sparkles className="h-3.5 w-3.5" />
                  {slide.eyebrow}
                </div>

                <div
                  key={slide.id}
                  className="animate-[simplerFadeUp_.45s_ease-out]"
                >
                  <h2
                    className={[
                      "max-w-[650px]",
                      "text-[20px] font-semibold leading-[1.15] tracking-[-0.03em]",
                      "text-slate-950 dark:text-zinc-50 sm:text-[23px] lg:text-[26px]",
                    ].join(" ")}
                  >
                    {slide.title}
                  </h2>

                  <p className="mt-3 max-w-[610px] text-[13px] leading-6 text-slate-500 dark:text-zinc-300/85 sm:text-[13px]">
                    {slide.description}
                  </p>
                </div>

                <div className="mt-6 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    onClick={handleHeroAction}
                    className={[
                      "inline-flex h-10 items-center gap-2 rounded-xl",
                      "bg-violet-600 px-4",
                      "text-[12px] font-semibold text-white",
                      "shadow-sm transition-all",
                      "hover:-translate-y-0.5 hover:bg-violet-700 hover:shadow-md",
                    ].join(" ")}
                  >
                    {slide.cta}
                    <ChevronRight className="h-4 w-4" />
                  </button>

                  <div className="flex items-center gap-2 text-[11px] text-slate-500 dark:text-zinc-300/80">
                    <span className="relative flex h-2 w-2">
                      <span className="absolute inline-flex h-full w-full animate-ping rounded-full bg-emerald-400 opacity-50" />
                      <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-500" />
                    </span>

                    Monitoramento ativo
                  </div>
                </div>
              </div>

              {/* CARROSSEL */}
              <div className="mt-7 flex items-center gap-3">
                <button
                  type="button"
                  aria-label="Destaque anterior"
                  onClick={previousSlide}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-violet-200 hover:text-violet-600 dark:border-white/10 dark:bg-[#151a23] dark:text-zinc-300 dark:hover:border-violet-400/30 dark:hover:bg-[#191e28] dark:hover:text-violet-300"
                >
                  <ChevronLeft className="h-4 w-4" />
                </button>

                <div className="flex items-center gap-1.5">
                  {slides.map((item, index) => (
                    <button
                      key={item.id}
                      type="button"
                      aria-label={`Ir para destaque ${index + 1}`}
                      aria-current={activeSlide === index ? "true" : undefined}
                      onClick={() => setActiveSlide(index)}
                      className={[
                        "h-1.5 rounded-full transition-all duration-300",
                        activeSlide === index
                          ? "w-7 bg-violet-600"
                          : "w-1.5 bg-slate-200 hover:bg-slate-300",
                      ].join(" ")}
                    />
                  ))}
                </div>

                <button
                  type="button"
                  aria-label="Próximo destaque"
                  onClick={nextSlide}
                  className="flex h-8 w-8 items-center justify-center rounded-full border border-slate-200 bg-white text-slate-500 transition hover:border-violet-200 hover:text-violet-600 dark:border-white/10 dark:bg-[#151a23] dark:text-zinc-300 dark:hover:border-violet-400/30 dark:hover:bg-[#191e28] dark:hover:text-violet-300"
                >
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>

            {/* DIREITA */}
            <div className="relative hidden min-h-[340px] lg:block">
              {/* VISUAL DE STATUS */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div
                  className={[
                    "relative h-[292px] w-[410px] overflow-visible",
                    "rounded-[38px] border border-transparent",
                    "bg-transparent shadow-none",
                  ].join(" ")}
                >
                  {/* SISI */}
                  <div
                    className={[
                      "absolute bottom-0 left-1/2 z-[3]",
                      "h-[300px] w-[258px] -translate-x-1/2",
                      "overflow-hidden rounded-[32px]",
                      "shadow-[0_22px_45px_rgba(76,29,149,0.20)]",
                    ].join(" ")}
                  >
                    {sisiImage ? (
                      <img
                        src={sisiImage}
                        alt="Sisi - Inteligência da Simpler"
                        className="h-full w-full object-cover object-center"
                      />
                    ) : (
                      <div
                        className={[
                          "flex h-full w-full items-center justify-center",
                          "rounded-[32px] bg-violet-950",
                        ].join(" ")}
                      >
                        <Bot
                          className="h-16 w-16 text-violet-200"
                          strokeWidth={1.5}
                        />
                      </div>
                    )}
                  </div>

                  {/* CARD 1 */}
                  <button
                    type="button"
                    onClick={() => setDrawer("fiscal")}
                    className={[
                      "absolute -left-16 top-7 z-[5]",
                      "flex items-center gap-2 rounded-xl border border-slate-200 dark:border-white/10",
                      "bg-white px-3 py-2.5 dark:bg-[#151a23]",
                      "shadow-[0_10px_30px_rgba(15,23,42,0.08)]",
                      "transition hover:-translate-y-1 hover:border-violet-200",
                    ].join(" ")}
                  >
                    <div
                      className={[
                        "flex h-8 w-8 items-center justify-center rounded-lg",
                        Number(data.fiscalPending) > 0
                          ? "bg-red-50 text-red-600 dark:bg-red-500/10 dark:text-red-300"
                          : "bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300",
                      ].join(" ")}
                    >
                      {Number(data.fiscalPending) > 0 ? (
                        <AlertTriangle className="h-4 w-4" />
                      ) : (
                        <CheckCircle2 className="h-4 w-4" />
                      )}
                    </div>

                    <div className="text-left">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Situação fiscal
                      </div>
                      <div className="text-[13px] font-bold text-slate-900 dark:text-zinc-100">
                        {Number(data.fiscalPending) || 0}{" "}
                        {Number(data.fiscalPending) === 1
                          ? "pendente"
                          : "pendentes"}
                      </div>
                    </div>
                  </button>

                  {/* CARD 2 */}
                  <button
                    type="button"
                    onClick={() => setDrawer("obligations")}
                    className={[
                      "absolute -right-16 top-[82px] z-[5]",
                      "flex items-center gap-2 rounded-xl border border-slate-200 dark:border-white/10",
                      "bg-white px-3 py-2.5 dark:bg-[#151a23]",
                      "shadow-[0_10px_30px_rgba(15,23,42,0.08)]",
                      "transition hover:-translate-y-1 hover:border-violet-200",
                    ].join(" ")}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-violet-50 text-violet-600 dark:bg-violet-500/10 dark:text-violet-300">
                      <ReceiptText className="h-4 w-4" />
                    </div>

                    <div className="text-left">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Obrigações
                      </div>
                      <div className="text-[13px] font-bold text-slate-900 dark:text-zinc-100">
                        {data.obligations}{" "}
                        {Number(data.obligations) === 1
                          ? "pendência"
                          : "pendências"}
                      </div>
                    </div>
                  </button>

                  {/* CARD 3 */}
                  <div
                    className={[
                      "absolute bottom-7 -left-12 z-[5]",
                      "flex items-center gap-2 rounded-xl border border-slate-200 dark:border-white/10",
                      "bg-white px-3 py-2.5 dark:bg-[#151a23]",
                      "shadow-[0_10px_30px_rgba(15,23,42,0.08)]",
                    ].join(" ")}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-emerald-50 text-emerald-600 dark:bg-emerald-500/10 dark:text-emerald-300">
                      <CheckCircle2 className="h-4 w-4" />
                    </div>

                    <div>
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        e-CAC
                      </div>
                      <div className="text-[13px] font-bold text-slate-900 dark:text-zinc-100">
                        Sincronizado
                      </div>
                    </div>
                  </div>

                  {/* CARD 4 */}
                  <button
                    type="button"
                    onClick={() => setDrawer("certificates")}
                    className={[
                      "absolute -right-12 bottom-6 z-[5]",
                      "flex items-center gap-2 rounded-xl border border-slate-200 dark:border-white/10",
                      "bg-white px-3 py-2.5 dark:bg-[#151a23]",
                      "shadow-[0_10px_30px_rgba(15,23,42,0.08)]",
                      "transition hover:-translate-y-1 hover:border-violet-200",
                    ].join(" ")}
                  >
                    <div className="flex h-8 w-8 items-center justify-center rounded-lg bg-amber-50 text-amber-600 dark:bg-amber-500/10 dark:text-amber-300">
                      <ShieldCheck className="h-4 w-4" />
                    </div>

                    <div className="text-left">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Certidões
                      </div>
                      <div className="text-[13px] font-bold text-slate-900 dark:text-zinc-100">
                        {data.certificates}{" "}
                        {Number(data.certificates) === 1
                          ? "irregular"
                          : "irregulares"}
                      </div>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ==========================================================
            ATALHOS OPERACIONAIS
        ========================================================== */}
        <div
          className={[
            "grid grid-cols-3 gap-1 rounded-[22px]",
            "border border-slate-200 bg-white px-2 py-2 dark:border-white/10 dark:bg-[#11151d]",
            "shadow-[0_8px_30px_rgba(15,23,42,0.045)]",
            "sm:grid-cols-6",
          ].join(" ")}
        >
          <QuickAction
            icon={Mail}
            title="Caixa Postal"
            subtitle="Comunicações"
            count={data.messages}
            active={drawer === "messages"}
            onClick={() => setDrawer("messages")}
          />

          <QuickAction
            icon={Landmark}
            title="Fiscal"
            subtitle="Ocorrências"
            count={data.critical}
            active={drawer === "fiscal"}
            onClick={() => setDrawer("fiscal")}
          />

          <QuickAction
            icon={ReceiptText}
            title="Obrigações"
            subtitle="Prazos e entregas"
            count={data.obligations}
            active={drawer === "obligations"}
            onClick={() => setDrawer("obligations")}
          />

          <QuickAction
            icon={ShieldCheck}
            title="Certidões"
            subtitle="Regularidade"
            count={data.certificates}
            active={drawer === "certificates"}
            onClick={() => setDrawer("certificates")}
          />

          <QuickAction
            icon={CircleDollarSign}
            title="Financeiro"
            subtitle="Débitos fiscais"
            count={data.financial}
            active={drawer === "financial"}
            onClick={() => setDrawer("financial")}
          />

          <QuickAction
            icon={Bot}
            title="Sisi"
            subtitle="Inteligência fiscal"
            active={drawer === "sisi"}
            onClick={() => setDrawer("sisi")}
          />
        </div>
      </section>

      {/* ============================================================
          OVERLAY + DRAWER
      ============================================================ */}
      {currentDrawer && (
        <ScreenOverlay><div className="fixed inset-0 z-[90]">
          <button
            type="button"
            aria-label="Fechar painel"
            onClick={() => setDrawer(null)}
            className="absolute inset-0 bg-transparent backdrop-blur-0"
          />

          <aside
            className={[
              "absolute bottom-0 right-0 top-0",
              "w-full max-w-[430px]",
              "border-l border-slate-200 bg-slate-50",
              "shadow-[-20px_0_60px_rgba(15,23,42,0.12)]",
              "animate-[simplerDrawerIn_.28s_ease-out]",
              "rounded-[28px] overflow-hidden border-l border-slate-200 bg-white shadow-[0_18px_45px_rgba(15,23,42,0.08)]",
            ].join(" ")}
          >
            <div className="flex h-full flex-col">
              <div className="border-b border-slate-200 bg-white px-5 pb-5 pt-5">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex items-center gap-3">
                    <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-violet-50 text-violet-600">
                      <currentDrawer.icon className="h-5 w-5" />
                    </div>

                    <div>
                      <h3 className="text-[15px] font-semibold text-slate-950">
                        {currentDrawer.title}
                      </h3>

                      <p className="mt-0.5 text-[11px] text-slate-500">
                        {currentDrawer.description}
                      </p>
                    </div>
                  </div>

                  <button
                    type="button"
                    aria-label="Fechar painel"
                    onClick={() => setDrawer(null)}
                    className="flex h-9 w-9 items-center justify-center rounded-xl border border-slate-200 bg-white text-slate-500 transition hover:bg-slate-50 hover:text-slate-900"
                  >
                    <X className="h-4 w-4" />
                  </button>
                </div>

                {drawer === "certificates" ? (
                  <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Empresas
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-slate-950">
                        {formatNumber(data.companies)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-3">
                      <div className="text-[10px] text-emerald-600/70">
                        Regulares
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-emerald-600">
                        {formatNumber(data.certificatesRegular)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-red-200 bg-red-50/60 px-3 py-3">
                      <div className="text-[10px] text-red-500/70">
                        Irregulares
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-red-600">
                        {formatNumber(data.certificates)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Atualização
                      </div>
                      <div className="mt-2 text-[11px] font-semibold text-emerald-600">
                        {data.lastSync}
                      </div>
                    </div>
                  </div>
                ) : drawer === "fiscal" ? (
                  <div className="mt-5 grid grid-cols-2 gap-2 sm:grid-cols-4">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Empresas
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-slate-950">
                        {formatNumber(data.companies)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-emerald-200 bg-emerald-50/60 px-3 py-3">
                      <div className="text-[10px] text-emerald-600/70">
                        Regulares
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-emerald-600">
                        {formatNumber(data.fiscalRegular)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-red-200 bg-red-50/60 px-3 py-3">
                      <div className="text-[10px] text-red-500/70">
                        Pendentes
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-red-600">
                        {formatNumber(data.fiscalPending)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Atualização
                      </div>
                      <div className="mt-2 text-[11px] font-semibold text-emerald-600">
                        {data.lastSync}
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className="mt-5 grid grid-cols-3 gap-2">
                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Empresas
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-slate-950">
                        {formatNumber(data.companies)}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        {drawer === "obligations"
                          ? "Pendências"
                          : "Atenções"}
                      </div>
                      <div className="mt-1 text-lg font-bold tracking-tight text-slate-950">
                        {drawer === "obligations"
                          ? data.obligations
                          : data.critical}
                      </div>
                    </div>

                    <div className="rounded-xl border border-slate-200 bg-slate-50 px-3 py-3">
                      <div className="text-[10px] text-slate-400 dark:text-zinc-400">
                        Atualização
                      </div>
                      <div className="mt-2 text-[11px] font-semibold text-emerald-600">
                        {data.lastSync}
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <div className="flex-1 overflow-y-auto p-4">
                {currentDrawer.items.length > 0 ? (
                  <div className="space-y-2">
                    {currentDrawer.items.map((item) => (
                      <DrawerItem
                        key={item.id}
                        item={item}
                        onOpen={() => {
                          if (currentDrawer.route) {
                            onNavigate?.(currentDrawer.route);
                          }
                        }}
                      />
                    ))}
                  </div>
                ) : (
                  <div className="flex min-h-[300px] flex-col items-center justify-center text-center">
                    <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-emerald-50 text-emerald-600">
                      <CheckCircle2 className="h-6 w-6" />
                    </div>

                    <p className="mt-4 text-sm font-semibold text-slate-900">
                      Tudo certo por aqui
                    </p>

                    <p className="mt-1 max-w-[260px] text-xs leading-5 text-slate-500">
                      Nenhuma ocorrência importante foi encontrada nesta categoria.
                    </p>
                  </div>
                )}
              </div>

              <div className="border-t border-slate-200 bg-white p-4">
                <button
                  type="button"
                  onClick={() => {
                    if (currentDrawer.route) {
                      onNavigate?.(currentDrawer.route);
                    }
                  }}
                  className={[
                    "flex h-11 w-full items-center justify-center gap-2 rounded-xl",
                    "bg-slate-950 text-[12px] font-semibold text-white",
                    "transition hover:bg-violet-700",
                  ].join(" ")}
                >
                  Ver tudo em {currentDrawer.title}
                  <ChevronRight className="h-4 w-4" />
                </button>
              </div>
            </div>
          </aside>
        </div></ScreenOverlay>
      )}

      {/* animações sem dependência extra */}
      <style>{`
        @keyframes simplerFadeUp {
          from {
            opacity: 0;
            transform: translateY(8px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes simplerDrawerIn {
          from {
            opacity: 0;
            transform: translateX(32px);
          }
          to {
            opacity: 1;
            transform: translateX(0);
          }
        }
      `}</style>
    </>
  );
}
